import ThemeModal from "components/Common/ThemeModal"
import { Badge, Col, Row, Spinner } from "reactstrap"
import { formatReadableDate } from "utils/formatDate"
import { formatCurrency } from "utils/formatCurrency"
import { getEntityLabel, getUserLabel } from "pages/AccountPages/utils/displayValue"

const getMoneyDisplayValue = (value, currency = "BDT") => formatCurrency(value || 0, currency)

const ReceiptRow = ({ label, value, mono = false, strong = false }) => (
  <div className="d-flex align-items-start justify-content-between gap-3 py-2" style={{ borderBottom: "1px dashed rgba(15, 23, 42, 0.12)" }}>
    <span className="text-muted text-uppercase fw-semibold" style={{ fontSize: 10, letterSpacing: 1 }}>{label}</span>
    <span className={`${mono ? "font-monospace" : ""} ${strong ? "fw-bold" : "fw-semibold"} text-dark text-end`.trim()} style={{ fontSize: strong ? 15 : 14 }}>
      {value || "-"}
    </span>
  </div>
)

const ReceiptSection = ({ title, children }) => (
  <div className="pt-3">
    <div className="text-uppercase text-muted fw-semibold mb-2" style={{ fontSize: 11, letterSpacing: 1 }}>{title}</div>
    {children}
  </div>
)

const AccountFundTransferView = ({ modal, toggleModal, transfer, loading }) => {
  const fromAccountName = getEntityLabel(transfer?.from_account || transfer?.fromAccount, transfer?.from_account_id, "Account")
  const toAccountName = getEntityLabel(transfer?.to_account || transfer?.toAccount, transfer?.to_account_id, "Account")
  const paymentMethodName = getEntityLabel(transfer?.payment_method || transfer?.paymentMethod, transfer?.payment_method_id, "Payment Method")
  const createdBy = getUserLabel(transfer?.createdBy || transfer?.created_by)
  const updatedBy = getUserLabel(transfer?.updatedBy || transfer?.updated_by)

  return (
    <ThemeModal isOpen={modal} toggle={toggleModal} title="Fund Transfer Details" size="xl" modalBodyClass="bg-body-tertiary">
      {loading ? (
        <div className="text-center py-4"><Spinner size="sm" /></div>
      ) : !transfer ? (
        <div className="text-center py-4 text-muted">No details available.</div>
      ) : (
        <div className="mx-auto" style={{ maxWidth: 740 }}>
          {transfer.is_reversed && (
            <div className="alert alert-warning d-flex align-items-center gap-2 mb-3 py-2 px-3 rounded-3" style={{ fontSize: 13 }}>
              <i className="bx bx-info-circle fs-5" />
              <span>This fund transfer has been <strong>reversed</strong>. The transfer has been cancelled by a ledger reversal entry.</span>
            </div>
          )}
          <div className="rounded-4 border overflow-hidden" style={{ background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)", borderColor: "rgba(15, 23, 42, 0.08)", boxShadow: "0 24px 64px rgba(15, 23, 42, 0.08)" }}>
            <div className="px-4 py-2 d-flex align-items-center justify-content-between" style={{ background: "linear-gradient(90deg, rgba(15, 23, 42, 0.96) 0%, rgba(30, 41, 59, 0.96) 100%)" }}>
              <div className="text-white text-uppercase fw-semibold" style={{ fontSize: 10, letterSpacing: 1.6 }}>Fund Transfer</div>
              <div className="text-white-50 font-monospace" style={{ fontSize: 12 }}>AFT-{String(transfer.id ?? "0000").padStart(4, "0")}</div>
            </div>

            <div className="px-4 py-3 border-bottom" style={{ background: "radial-gradient(circle at top left, rgba(16, 185, 129, 0.12), transparent 34%), linear-gradient(180deg, #f8fbfd 0%, #ffffff 100%)", borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
                <div>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Fund transfer</div>
                  <div className="h5 mb-1 text-dark" style={{ letterSpacing: -0.2 }}>
                    {fromAccountName} <span className="text-muted mx-2" style={{ fontSize: 14 }}>→</span> {toAccountName}
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <Badge color="info" pill className="px-2 py-1">Transferred</Badge>
                    {transfer.is_reversed && <Badge color="secondary" pill className="px-2 py-1">Reversed</Badge>}
                    <span className="text-muted small font-monospace">#{transfer.id ?? "-"}</span>
                    <span className="text-muted small">{transfer.transfer_date ? formatReadableDate(transfer.transfer_date) : "No date"}</span>
                  </div>
                </div>
                <div className="text-md-end">
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Amount</div>
                  <div className="fw-bold text-dark" style={{ fontSize: 34, lineHeight: 0.95, letterSpacing: -1 }}>{getMoneyDisplayValue(transfer.amount)}</div>
                  <div className="text-muted small mt-1">on {transfer.transfer_date ? formatReadableDate(transfer.transfer_date) : "-"}</div>
                </div>
              </div>
            </div>

            <div className="position-relative" style={{ height: 18, background: "#fff" }}>
              <div style={{ position: "absolute", left: 0, right: 0, top: "50%", borderTop: "1px dashed rgba(15, 23, 42, 0.18)" }} />
              <div className="position-absolute rounded-circle bg-body-tertiary" style={{ width: 22, height: 22, left: -11, top: -2 }} />
              <div className="position-absolute rounded-circle bg-body-tertiary" style={{ width: 22, height: 22, right: -11, top: -2 }} />
            </div>

            <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <Row className="g-3 g-md-4">
                <Col md={6}>
                  <ReceiptSection title="Transfer Details">
                    <ReceiptRow label="From Account" value={fromAccountName} />
                    <ReceiptRow label="To Account" value={toAccountName} />
                    <ReceiptRow label="Payment Method" value={paymentMethodName} />
                    <ReceiptRow label="Transfer Date" value={transfer.transfer_date ? formatReadableDate(transfer.transfer_date) : "-"} />
                    <ReceiptRow label="Amount" value={getMoneyDisplayValue(transfer.amount)} mono />
                  </ReceiptSection>
                </Col>
                <Col md={6}>
                  <ReceiptSection title="Record Info">
                    <ReceiptRow label="Record ID" value={transfer.id ?? "-"} mono />
                    <ReceiptRow label="Reference No." value={transfer.reference_number} />
                    <ReceiptRow label="Created By" value={createdBy} />
                    <ReceiptRow label="Updated By" value={updatedBy} />
                  </ReceiptSection>
                </Col>
              </Row>
            </div>

            <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <ReceiptSection title="Transfer Ledger">
                <div className="rounded-3 px-3 py-2" style={{ background: "rgba(248, 250, 252, 0.9)", border: "1px solid rgba(15, 23, 42, 0.06)" }}>
                  <ReceiptRow label="Debit (From)" value={fromAccountName} />
                  <ReceiptRow label="Credit (To)" value={toAccountName} />
                  <ReceiptRow label="Transfer Date" value={transfer.transfer_date ? formatReadableDate(transfer.transfer_date) : "-"} />
                  <ReceiptRow label="Total Transferred" value={getMoneyDisplayValue(transfer.amount)} strong mono />
                  <ReceiptRow label="Ledger Status" value={transfer.is_reversed ? "⚠ Reversed — Cancelled by reversal entry" : "Active"} />
                </div>
              </ReceiptSection>
            </div>

            {transfer.description && (
              <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
                <ReceiptSection title="Description">
                  <div className="text-dark rounded-3 px-3 py-2" style={{ whiteSpace: "pre-wrap", lineHeight: 1.5, background: "linear-gradient(180deg, rgba(248, 250, 252, 0.95) 0%, rgba(255, 255, 255, 1) 100%)", border: "1px solid rgba(15, 23, 42, 0.06)" }}>
                    {transfer.description}
                  </div>
                </ReceiptSection>
              </div>
            )}

            <div className="px-4 py-3" style={{ background: "linear-gradient(180deg, rgba(248, 250, 252, 0.75) 0%, rgba(241, 245, 249, 0.9) 100%)" }}>
              <Row className="g-3">
                <Col md={4}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Created</div>
                  <div className="small text-dark">{createdBy}</div>
                  <div className="small text-muted">{transfer.created_at ? formatReadableDate(transfer.created_at) : "-"}</div>
                </Col>
                <Col md={4}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Updated</div>
                  <div className="small text-dark">{updatedBy}</div>
                  <div className="small text-muted">{transfer.updated_at ? formatReadableDate(transfer.updated_at) : "-"}</div>
                </Col>
                <Col md={4}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Status</div>
                  <div className="small text-dark fw-semibold">{transfer.deleted_at ? "Archived" : "Active"}</div>
                  <div className="small text-muted">{transfer.deleted_at ? formatReadableDate(transfer.deleted_at) : "No deletion recorded"}</div>
                </Col>
              </Row>
            </div>

          </div>
        </div>
      )}
    </ThemeModal>
  )
}

export default AccountFundTransferView
